/**
 * PropertyFormulaService
 *
 * Manages formula property computations - calculating values from other properties.
 *
 * Features:
 * - Formula configuration management (CRUD)
 * - Server-side computation via Edge Function (safe AST evaluation)
 * - Local evaluation fallback for simple formulas
 * - Dependency tracking and validation
 * - Cache management with invalidation
 * - Circular dependency detection
 *
 * Examples:
 * - {price} * {quantity}
 * - if({isPremium}, {basePrice} * 1.5, {basePrice})
 * - round({hours} / {days}, 2)
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { restSelect, restInsert, restUpdate, restDelete } from '../lib/supabaseRest';
import type {
  PropertyFormula,
  PropertyComputedCache,
  CreateFormulaRequest,
  ComputeFormulaResponse,
  FormulaResultType,
} from '../types/advanced-properties';

// Edge Function URL
const EDGE_FUNCTION_URL = import.meta.env.VITE_SUPABASE_URL
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'http://localhost:54321/functions/v1';

export class PropertyFormulaService {
  /**
   * Get formula configuration for a property
   */
  async getFormula(propertyId: string): Promise<PropertyFormula | null> {
    try {
      const formula = await db.propertyFormulas.get({ propertyId });
      return formula || null;
    } catch (error) {
      console.error('[PropertyFormulaService] Error getting formula:', error);
      return null;
    }
  }

  /**
   * Create a new formula configuration
   */
  async createFormula(request: CreateFormulaRequest): Promise<PropertyFormula> {
    try {
      // Validate formula syntax and detect circular dependencies
      const validation = await this.validateFormula(
        request.expression,
        request.property_id
      );

      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid formula');
      }

      const formula: PropertyFormula = {
        id: this.generateId(),
        property_id: request.property_id,
        expression: request.expression,
        dependencies: validation.dependencies || [],
        result_type: request.result_type || 'number',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Store in Supabase
      const created = await restInsert<PropertyFormula>('property_formulas', [formula]);

      // Store locally
      await db.propertyFormulas.add(formula);

      // Emit event
      eventBus.emit('formula:created', {
        propertyId: formula.property_id,
        formula: created[0],
      });

      return created[0];
    } catch (error) {
      console.error('[PropertyFormulaService] Error creating formula:', error);
      throw error;
    }
  }

  /**
   * Update formula expression
   */
  async updateFormula(
    formulaId: string,
    expression: string
  ): Promise<PropertyFormula> {
    try {
      const formula = await db.propertyFormulas.get(formulaId);
      if (!formula) {
        throw new Error('Formula not found');
      }

      // Validate new expression
      const validation = await this.validateFormula(expression, formula.property_id);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid formula');
      }

      const updatedData = {
        expression,
        dependencies: validation.dependencies || [],
        updated_at: new Date().toISOString(),
      };

      // Update in Supabase
      await restUpdate(
        'property_formulas',
        updatedData,
        { eq: { id: formulaId } }
      );

      // Update locally
      await db.propertyFormulas.update(formulaId, updatedData);

      // Invalidate cache
      await this.invalidateCache(formula.property_id);

      // Emit event
      eventBus.emit('formula:updated', { formulaId, expression });

      // Fetch updated formula
      const updated = await db.propertyFormulas.get(formulaId);
      if (!updated) {
        throw new Error('Formula not found after update');
      }

      return updated;
    } catch (error) {
      console.error('[PropertyFormulaService] Error updating formula:', error);
      throw error;
    }
  }

  /**
   * Delete a formula configuration
   */
  async deleteFormula(formulaId: string): Promise<void> {
    try {
      const formula = await db.propertyFormulas.get(formulaId);
      if (!formula) return;

      // Delete from Supabase
      await restDelete('property_formulas', { eq: { id: formulaId } });

      // Delete locally
      await db.propertyFormulas.delete(formulaId);

      // Clear cache
      await this.clearCache(formula.property_id);

      // Emit event
      eventBus.emit('formula:deleted', {
        propertyId: formula.property_id,
        formulaId,
      });
    } catch (error) {
      console.error('[PropertyFormulaService] Error deleting formula:', error);
      throw error;
    }
  }

  /**
   * Compute formula value for a specific object
   * Tries server-side Edge Function first, falls back to local computation for simple formulas
   */
  async computeFormula(
    propertyId: string,
    objectId: string
  ): Promise<ComputeFormulaResponse> {
    try {
      // Check cache first
      const cached = await this.getCache(propertyId, objectId);
      if (cached && !cached.is_stale) {
        const formula = await this.getFormula(propertyId);
        return {
          value: cached.computed_value,
          dependencies: formula?.dependencies || [],
          expression: formula?.expression || '',
        };
      }

      // Get formula configuration
      const formula = await this.getFormula(propertyId);
      if (!formula) {
        throw new Error('Formula configuration not found');
      }

      // Try server-side computation first
      try {
        const result = await this.computeFormulaServerSide(formula, objectId);
        await this.setCache(propertyId, objectId, result.value);
        return result;
      } catch (serverError) {
        console.warn('[PropertyFormulaService] Server-side computation failed, trying local:', serverError);

        // Fall back to local computation (limited support)
        const result = await this.computeFormulaLocally(formula, objectId);
        await this.setCache(propertyId, objectId, result.value);
        return result;
      }
    } catch (error) {
      console.error('[PropertyFormulaService] Error computing formula:', error);
      throw error;
    }
  }

  /**
   * Compute formula using Edge Function
   */
  private async computeFormulaServerSide(
    formula: PropertyFormula,
    objectId: string
  ): Promise<ComputeFormulaResponse> {
    const response = await fetch(`${EDGE_FUNCTION_URL}/compute-formula`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`,
      },
      body: JSON.stringify({
        propertyId: formula.property_id,
        objectId,
        expression: formula.expression,
        dependencies: formula.dependencies,
      }),
    });

    if (!response.ok) {
      throw new Error(`Edge Function error: ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Computation failed');
    }

    return {
      value: data.data.value,
      dependencies: formula.dependencies,
      expression: formula.expression,
    };
  }

  /**
   * Compute formula locally (limited support for simple formulas)
   */
  private async computeFormulaLocally(
    formula: PropertyFormula,
    objectId: string
  ): Promise<ComputeFormulaResponse> {
    // Get property values for dependencies
    const context: Record<string, any> = {};

    for (const depName of formula.dependencies) {
      const props = await db.properties
        .where({ objectId, name: depName })
        .toArray();

      if (props.length > 0) {
        const prop = props[0];
        context[depName] = prop.value?.value || prop.value?.computed || null;
      }
    }

    // Try to evaluate simple formulas locally
    // Note: This is a simplified evaluator - complex formulas should use Edge Function
    const value = this.evaluateSimpleFormula(formula.expression, context);

    return {
      value,
      dependencies: formula.dependencies,
      expression: formula.expression,
    };
  }

  /**
   * Simple formula evaluator for basic arithmetic
   * WARNING: This is a fallback for offline mode - use Edge Function for full support
   */
  private evaluateSimpleFormula(
    expression: string,
    context: Record<string, any>
  ): number | string | boolean | null {
    try {
      // Replace property references {name} with actual values
      let evaluableExpr = expression;

      for (const [key, value] of Object.entries(context)) {
        const placeholder = `{${key}}`;
        const replacement = typeof value === 'string' ? `"${value}"` : String(value);
        evaluableExpr = evaluableExpr.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), replacement);
      }

      // Only allow simple arithmetic operations (no function calls)
      // This is a security-conscious subset - complex formulas need Edge Function
      if (!/^[\d\s+\-*/.()]+$/.test(evaluableExpr)) {
        throw new Error('Complex formula requires server-side evaluation');
      }

      // Use Function constructor (safer than eval, but still limited to simple math)
      // eslint-disable-next-line no-new-func
      const result = new Function(`return ${evaluableExpr}`)();

      return result;
    } catch (error) {
      console.error('[PropertyFormulaService] Error evaluating simple formula:', error);
      return null;
    }
  }

  /**
   * Validate formula syntax and dependencies
   */
  async validateFormula(
    expression: string,
    propertyId?: string,
    objectId?: string
  ): Promise<{ valid: boolean; error?: string; dependencies?: string[] }> {
    try {
      const response = await fetch(`${EDGE_FUNCTION_URL}/validate-formula`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`,
        },
        body: JSON.stringify({
          expression,
          propertyId,
          objectId,
        }),
      });

      if (!response.ok) {
        return {
          valid: false,
          error: 'Validation service unavailable',
        };
      }

      const data = await response.json();

      if (!data.success) {
        return {
          valid: false,
          error: data.error || 'Validation failed',
        };
      }

      return {
        valid: data.data.valid,
        error: data.data.errors?.[0],
        dependencies: data.data.dependencies,
      };
    } catch (error) {
      console.error('[PropertyFormulaService] Error validating formula:', error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Validation failed',
      };
    }
  }

  /**
   * Extract property dependencies from formula expression
   * (Client-side parsing for quick feedback)
   */
  extractDependencies(expression: string): string[] {
    const matches = expression.match(/\{([^}]+)\}/g);
    if (!matches) return [];

    return matches
      .map(match => match.replace(/[{}]/g, ''))
      .filter((value, index, self) => self.indexOf(value) === index); // unique
  }

  /**
   * Get cached computed value
   */
  private async getCache(
    propertyId: string,
    objectId: string
  ): Promise<PropertyComputedCache | null> {
    try {
      const cache = await db.propertyComputedCache
        .where({ propertyId, objectId })
        .first();
      return cache || null;
    } catch (error) {
      console.error('[PropertyFormulaService] Error getting cache:', error);
      return null;
    }
  }

  /**
   * Set cached computed value
   */
  private async setCache(
    propertyId: string,
    objectId: string,
    value: any
  ): Promise<void> {
    try {
      const cache: PropertyComputedCache = {
        id: this.generateId(),
        property_id: propertyId,
        object_id: objectId,
        computed_value: value,
        computed_at: new Date().toISOString(),
        is_stale: false,
      };

      // Upsert in Supabase
      await restInsert<PropertyComputedCache>('property_computed_cache', [cache], {
        onConflict: 'property_id,object_id',
      });

      // Upsert locally
      const existing = await this.getCache(propertyId, objectId);
      if (existing) {
        await db.propertyComputedCache.update(existing.id, {
          computed_value: value,
          computed_at: cache.computed_at,
          is_stale: false,
        });
      } else {
        await db.propertyComputedCache.add(cache);
      }
    } catch (error) {
      console.error('[PropertyFormulaService] Error setting cache:', error);
      // Don't throw - caching is best-effort
    }
  }

  /**
   * Invalidate cache (mark as stale)
   */
  async invalidateCache(propertyId: string): Promise<void> {
    try {
      // Mark as stale in Supabase
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        { eq: { property_id: propertyId } }
      );

      // Mark as stale locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.update(cache.id, { is_stale: true });
      }
    } catch (error) {
      console.error('[PropertyFormulaService] Error invalidating cache:', error);
      // Don't throw - cache invalidation is best-effort
    }
  }

  /**
   * Clear cache entries for a property
   */
  private async clearCache(propertyId: string): Promise<void> {
    try {
      // Delete from Supabase
      await restDelete('property_computed_cache', { eq: { property_id: propertyId } });

      // Delete locally
      const caches = await db.propertyComputedCache
        .where({ propertyId })
        .toArray();

      for (const cache of caches) {
        await db.propertyComputedCache.delete(cache.id);
      }
    } catch (error) {
      console.error('[PropertyFormulaService] Error clearing cache:', error);
      // Don't throw
    }
  }

  /**
   * Generate a unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const propertyFormulaService = new PropertyFormulaService();
